import NiceModal from "@ebay/nice-modal-react";
import type { AnimatorCreateEmoteProps } from "@/animations/Animator";
import { canv } from "@/canv/global";
import {
    createBlobFromString,
    isPresentationStartEvent,
    isPresentationStopEvent,
    isTimerCreateEvent,
    isTimerPropertyEvent,
    isVoteCreateEvent,
    isVoteEndedEvent,
    logException,
    updateCommentsMessages,
} from "@/canv/utils";
import { AiAnswer } from "@/elements/aiAnswer";
import { eventEmitter } from "@/eventEmitter";
import type { EventStructure } from "@/eventManager/events/types";
import type { Group } from "@/group";
import { JoinPresentationModal } from "@/interface/ui/modals/JoinPresentationModal";
import type { AiModes, CreateSubMenuActions, EditSubMenuActions } from "@/interface/WidgetMenu";
import type { EntityBaseState } from "@/shared/types/entityState";
import { showSnackbar } from "@/snackbar/snackbar";
import timerController from "@/timerController/timerController";
import type { TimerEventUpdateType, TimerUpdatePayload } from "@/timerController/types";
import {
    handlePropertyBuild,
    handlePropertyUpdate,
    returnSanitizedCommentActionEvents,
    returnSanitizedVoteActionEvents,
} from "./helpers";
import type { FastMessageSubtype, IntercomResolvers, WSResponse } from "./types";

const SNACKBAR_TIMEOUT = 7000;

export class Intercom {
    socket: WebSocket | null;
    disabled: boolean;
    disableEvents: boolean;
    disableAiEvents: boolean;

    resolvers: IntercomResolvers = {
        initResolver: () => {},
        requestDeskResolver: () => {},
        pingResolver: () => {},
    };

    constructor() {
        this.socket = null;
        this.disabled = false; // это значение используется в коде и может меняться на true в рантайме;
        this.disableEvents = false; // выключает отправку events. Не выключает fast
        this.disableAiEvents = false;
    }

    reConnect(path: string) {
        return new Promise((resolve, reject) => {
            if (canv.intercom.socket != null) {
                canv.intercom.socket.close();
            }

            canv.intercom.socket = new WebSocket(path);
            canv.intercom.socket.binaryType = "blob";
            canv.intercom.socket.onopen = () => {
                resolve("connected");
            };
            canv.intercom.socket.onerror = () => {
                if (!canv.isReconnectionProcess) {
                    //canv.appInterface.reconnectPanel.show();
                }
                reject("error");
            };
            canv.intercom.socket.onclose = () => {
                if (!canv.isReconnectionProcess) {
                    //canv.appInterface.reconnectPanel.show();
                }
                reject("closed");
            };

            canv.intercom.socket.onmessage = canv.intercom.processResponse.bind(canv.intercom);
        });
    }

    async initMessage() {
        return new Promise((resolve) => {
            const data = {
                type: "init",
                board_uid: canv.getInitDataProp("boardUID"),
                token: canv.getInitDataProp("userToken"), // не используется
                boardAccessToken: canv.getInitDataProp("boardAccessToken"),
                jwt: canv.getInitDataProp("JWT"),
                clientUID: canv.getInitDataProp("clientUID"),
                prefix: canv.getInitDataProp("fePrefix"),

                signature: canv.getInitDataProp("signature"),
                temporary: canv.getInitDataProp("temporary"),
            };

            const blob = createBlobFromString(JSON.safeStringify(data));

            if (canv.intercom.socket) {
                canv.intercom.socket.send(blob);
            }

            canv.intercom.resolvers.initResolver = (data) => {
                resolve(data);
            };
        });
    }

    async requestDeskMessage() {
        return new Promise((resolve) => {
            const data = {
                type: "deskRequest",
                desc_uid: canv.getInitDataProp("boardUID"), // не используется
            };

            const blob = createBlobFromString(JSON.safeStringify(data));

            if (canv.intercom.socket) {
                canv.intercom.socket.send(blob);
            }

            canv.intercom.resolvers.requestDeskResolver = (data) => {
                try {
                    const parsed = JSON.parse(data);
                    resolve(parsed.data);
                } catch (error) {
                    if (error instanceof Error) {
                        logException(error);
                    }
                    // тут не пишем reject. Бесконечно висим с await requestDeskMessage в месте вызова.
                }
            };
        });
    }

    requestUsersMessage() {
        const data = {
            type: "boardUsersRequest",
        };

        const blob = createBlobFromString(JSON.safeStringify(data));

        if (canv.intercom.socket) {
            canv.intercom.socket.send(blob);
        }
    }

    async pingMessage() {
        return new Promise<void>((resolve, reject) => {
            const data = {
                type: "pingRequest",
            };

            const blob = createBlobFromString(JSON.safeStringify(data));

            if (canv.intercom.socket) {
                canv.intercom.socket.send(blob);
            }

            canv.intercom.resolvers.pingResolver = () => {
                resolve();
            };

            setTimeout(() => {
                reject();
            }, 5000);
        });
    }

    async eventMessage(currentRecord: EventStructure[]) {
        const data = {
            type: "event",
            data: currentRecord,
        };

        if (this.disabled || this.disableEvents) {
            return;
        }

        const blob = createBlobFromString(JSON.safeStringify(data));

        if (canv.intercom.socket) {
            canv.intercom.socket.send(blob);
        }
    }

    async selectMessage(selectedGroups: Group[]) {
        if (this.disabled) {
            return;
        }
        const uids: string[] = [];

        for (let i = 0; i < selectedGroups.length; i++) {
            uids.push(selectedGroups[i].UID);
        }

        const selectData = {
            sessionUID: canv.sessionUID,
            selectedGroups: uids,
        };

        this.fastMessage("select", selectData);
    }

    viewMessage(
        cursorPosition: { x: number; y: number },
        viewPosition: {
            viewportStartX: number;
            viewportStartY: number;
            viewportWidth: number;
            viewportHeight: number;
        },
    ) {
        if (canv.highloadMode) {
            return;
        }

        const viewData = {
            sessionUID: canv.sessionUID,
            name: canv.getInitDataProp("userName"),
            login: canv.getInitDataProp("userEmail"),
            token: canv.getInitDataProp("userToken"),
            cursorPosition: cursorPosition,
            viewPosition: viewPosition,
        };

        this.fastMessage("view", viewData);
    }

    fastMessage<T>(subtype: FastMessageSubtype, content: T) {
        const data = {
            type: "fast",
            subtype: subtype,
            data: content,
        };

        if (this.disabled) {
            return;
        }

        const blob = createBlobFromString(JSON.safeStringify(data));

        if (canv.intercom.socket) {
            canv.intercom.socket.send(blob);
        }
    }

    async sendEmoteMessage(x: number, y: number, url: string, type: string = "emote") {
        if (this.disabled) {
            return;
        }

        const data = {
            type: type,
            x: x,
            y: y,
            url: url,
        };

        const blob = createBlobFromString(JSON.safeStringify(data));

        if (canv.intercom.socket) {
            canv.intercom.socket.send(blob);
        }
    }

    async sendAiMessage(
        mode: AiModes,
        action: CreateSubMenuActions | EditSubMenuActions,
        selectedUIDs: string[],
        targetWidgetUID?: string,
        aiBeforeState?: EntityBaseState[],
    ) {
        if (this.disableAiEvents) {
            return;
        }
        //Для включения undo/redo-раскомментировать строки
        /* this.aiBeforeState = aiBeforeState; */

        const data = {
            type: "aiRequest",
            mode,
            action,
            selectedUIDs,
            targetWidgetUID,
        };

        const blob = createBlobFromString(JSON.safeStringify(data));

        if (canv.intercom.socket) {
            canv.intercom.socket.send(blob);
        }
    }

    async processResponse(event: MessageEvent) {
        const messageStr = await new Response(event.data).text();

        let messageOb: WSResponse;

        try {
            messageOb = JSON.parse(messageStr);
        } catch {
            logException(new Error("websocket response bad format"));
            return;
        }

        if (messageOb.status === "failed") {
            logException(new Error("got status failed"));
            return;
        }

        if (messageOb.type === "descResponse") {
            canv.intercom.resolvers.requestDeskResolver(messageStr);
        }

        if (messageOb.type === "pingResponse") {
            canv.intercom.resolvers.pingResolver();
        }

        if (messageOb.type === "eventResponse" || messageOb.type === "event") {
            const messageData =
                typeof messageOb.data === "string"
                    ? JSON.parse(messageOb.data)?.data
                    : messageOb.data;

            const applyState = (item: (typeof messageData)[number]) => {
                const widget = canv.canvasController.FindByUID(item.UID);
                if (!widget) return;

                widget.loadState(item.state, item.fixType);
            };

            const isAi = messageOb.source === "ai";

            // выбираем, что обрабатывать
            const itemsToProcess = isAi
                ? messageData
                : messageData.filter((item) => item.state?.entityType === "aiAnswer");

            // Для undo/redo
            // if (itemsToProcess.length) {
            //     canv.eventManager.createAndInsertAiTwinRecord(this.aiBeforeState, itemsToProcess);
            // }

            itemsToProcess.forEach(applyState);

            // this.aiBeforeState = [];
        }

        if (messageOb.type === "eventResponse") {
            try {
                canv.eventManager.processEventConfirm(messageOb.isApplied);
            } catch (error) {
                if (error instanceof Error) {
                    logException(error);
                }

                return;
            }
            canv.redraw();
        }

        if (messageOb.type === "initResponse") {
            canv.intercom.resolvers.initResolver(messageOb);
        }

        if (messageOb.type === "eventResponse") {
            const messageData = JSON.parse(messageOb.data)?.data;
            const isApplied = messageOb.isApplied;
            try {
                if (Array.isArray(messageData)) {
                    if (isTimerPropertyEvent(messageData) && isApplied) {
                        this.updateTimer(messageData);
                    }
                    if (messageData[0] && isTimerCreateEvent(messageData[0]) && isApplied) {
                        timerController.setTimer(messageData[0].entities[0]);
                    }

                    this.updateCommentsWindowData(messageData, isApplied);

                    this.updatePresentationWindow(messageData);

                    if (isVoteCreateEvent(messageData[0])) {
                        canv.appInterface.voteWindow.close();
                        canv.canvasController.addVote(messageData[0].entities[0]);
                        this.updateActiveVoteWindow(messageData, isApplied);
                    }

                    if (isVoteEndedEvent(messageData[0]) && canv.canvasController.activeVote) {
                        const openResults = canv.appInterface.activeVote.isOpen;
                        canv.canvasController.deleteActiveVote();
                        canv.appInterface.activeVote.resetCurrentVoteInfo();
                        if (openResults) {
                            canv.appInterface.voteWindow.showResultState();
                        } else {
                            canv.appInterface.voteWindow.resetTimer();
                        }
                        canv.appInterface.voteWindow.startModal?.unmount();
                        canv.appInterface.voteWindow.startModal = null;
                    }

                    if (["property", "propertyBuild", "build"].includes(messageData[0].type)) {
                        if (
                            messageData[0]?.UID &&
                            messageData[0]?.UID === canv.canvasController?.activeVote?.UID
                        ) {
                            this.updateActiveVoteWindow(messageData, isApplied);
                        } else {
                            // todo добавить проверку, что это вообще событие голосования - достать из entities
                            this.updatePastVoteWindow(messageData, isApplied);
                        }
                    }

                    if (isPresentationStartEvent(messageData[0]) && isApplied) {
                        eventEmitter.dispatch("presentation.mode.visibility", { isVisible: true });
                        eventEmitter.dispatch("presentation.mode.presentationUID", {
                            UID: messageData[0].UID,
                        });
                    }
                    if (isPresentationStopEvent(messageData[0]) && isApplied) {
                        eventEmitter.dispatch("presentation.mode.visibility", { isVisible: false });
                        eventEmitter.dispatch("presentation.mode.presentationUID", {
                            UID: "",
                        });
                    }
                }
            } catch (err) {
                console.error(
                    "processResponse, ошибка обновления списка комментариев, голосования или презентации",
                    err,
                );
                canv.captureException(err);
            }
        }

        if (messageOb.type === "event") {
            const messageData = messageOb.data;
            const isApplied = true;
            if (!isVoteCreateEvent(messageData[0])) {
                try {
                    canv.eventManager.processIncomingRecord(
                        messageData as unknown as EventStructure[],
                    );
                } catch (error) {
                    if (error instanceof Error) {
                        logException(error);
                    }

                    return;
                }
                canv.redraw();
            }

            if (Array.isArray(messageData)) {
                try {
                    if (isTimerPropertyEvent(messageData) && isApplied) {
                        this.updateTimer(messageData);
                    }
                    if (messageData[0] && isTimerCreateEvent(messageData[0]) && isApplied) {
                        timerController.setTimer(messageData[0].entities[0]);
                    }

                    this.updateCommentsWindowData(messageData, isApplied);

                    this.updatePresentationWindow(messageData);
                } catch (err) {
                    console.error(
                        "processResponse, ошибка обновления списка комментариев/таймера/презентации",
                        err,
                    );
                    canv.captureException(err);
                }

                if (isVoteCreateEvent(messageData[0])) {
                    canv.appInterface.voteWindow.close();
                    canv.canvasController.addVote(messageData[0].entities[0]);
                    this.triggerStartVoteWindow(messageData, isApplied);
                }

                if (isVoteEndedEvent(messageData[0]) && canv.canvasController.activeVote) {
                    const openResults = canv.appInterface.activeVote.isOpen;
                    canv.canvasController.deleteActiveVote();
                    canv.appInterface.activeVote.resetCurrentVoteInfo();
                    if (openResults) {
                        canv.appInterface.voteWindow.showResultState();
                    } else {
                        canv.appInterface.voteWindow.resetTimer();
                    }
                    canv.appInterface.voteWindow.startModal?.unmount();
                    canv.appInterface.voteWindow.startModal = null;
                }

                if (["property", "propertyBuild", "build"].includes(messageData[0].type)) {
                    if (
                        messageData[0]?.UID &&
                        messageData[0]?.UID === canv.canvasController?.activeVote?.UID
                    ) {
                        this.updateActiveVoteWindow(messageData, isApplied);
                    } else {
                        this.updatePastVoteWindow(messageData, isApplied);
                    }
                }

                if (isPresentationStartEvent(messageData[0]) && isApplied) {
                    eventEmitter.dispatch("presentation.mode.presentationUID", {
                        UID: messageData[0].UID,
                    });
                    const otherUsers = canv.multiManager.multiUsersMap;
                    const speaker = Array.from(otherUsers.values()).find(
                        (user) => user.token === messageData[0].state.presenterToken,
                    );
                    NiceModal.show(JoinPresentationModal, {
                        actor: speaker?.name || "",
                        join: () =>
                            eventEmitter.dispatch("presentation.mode.visibility", {
                                isVisible: true,
                            }),
                    });
                }
                if (isPresentationStopEvent(messageData[0]) && isApplied) {
                    eventEmitter.dispatch("presentation.mode.visibility", { isVisible: false });
                    eventEmitter.dispatch("presentation.mode.presentationUID", { UID: "" });
                }
            }
        }

        if (messageOb.type === "fast") {
            if (messageOb.subtype === "view") {
                canv.multiManager.applyViewData(messageOb.data);
            } else if (messageOb.subtype === "select") {
                canv.multiManager.applySelectData(messageOb.data);
            } else if (messageOb.subtype === "cursor_follow_invite") {
                canv.appInterface.cursorTrackingWindow.open(messageOb.data.sessionUID);
            }
            canv.redraw();
        }

        if (messageOb.type === "emote") {
            canv.animator.createEmote(messageOb as unknown as AnimatorCreateEmoteProps);
        }

        if (messageOb.type === "emotePresentation") {
            document.dispatchEvent(
                new CustomEvent("reactionPresentationMessage", { detail: messageOb }),
            );
        }

        if (messageOb.type === "aiResponse") {
            this.processAiResponse(messageOb);
        }

        if (messageOb.type === "boardUsers" || messageOb.type === "boardUsersResponse") {
            const users = Array.isArray(messageOb.data?.users) ? messageOb.data.users : [];
            canv.multiManager.applyBoardUsers(users);
        }
    }

    updateTimer(messageData: EventStructure[]) {
        const entity = messageData[0];
        if (!entity) return;
        if (entity.type === "property") {
            timerController.updateTimer(
                entity.state as TimerUpdatePayload,
                entity.fixType as TimerEventUpdateType,
            );
        }
    }

    updateCommentsWindowData(messageData: EventStructure[], isApplied: boolean) {
        // !!! используем только sanitizedCommentsActions, использование messageData напрямую не безопасно
        const sanitizedCommentsActions = returnSanitizedCommentActionEvents(messageData);
        updateCommentsMessages(sanitizedCommentsActions, isApplied);
        // updateList должен быть под updateCommentsMessages, так как updateCommentsMessages обновляет список items на доске
        canv.appInterface.commentsWindow.updateList(sanitizedCommentsActions, isApplied);
    }

    updatePresentationWindow(messageData: EventStructure[]) {
        const needUpdate = messageData.some(
            (event) =>
                (event.type === "build" &&
                    event.entities.some((e) => e.entityType === "presentationWidget")) ||
                (event.type === "property" && event.state.entityType === "presentationWidget"),
        );

        messageData.forEach((entity) => {
            if (
                entity.type === "property" &&
                (entity.fixType === "zoomPresentation" || entity.fixType === "navigatePresentation")
            ) {
                document.dispatchEvent(
                    new CustomEvent("updatePresentationModeMessage", { detail: entity.state }),
                );
            }
        });

        messageData.forEach((entity) => {
            if (
                entity.type === "property" &&
                (entity.fixType === "joinPresentation" || entity.fixType === "leave")
            ) {
                document.dispatchEvent(
                    new CustomEvent("updatePresentationMembersCountMessage", {}),
                );
            }
        });

        if (needUpdate) {
            document.dispatchEvent(new CustomEvent("updatePresentationWindowMessage", {}));
        }
    }

    triggerStartVoteWindow(messageData: EventStructure[], isApplied: boolean) {
        const sanitizedVoteActions = returnSanitizedVoteActionEvents(messageData);

        if (sanitizedVoteActions.length && sanitizedVoteActions[0].vote && isApplied) {
            const { creatorName, duration, UID } = sanitizedVoteActions[0].vote;
            if (typeof duration !== "number" || !UID) return;
            canv.appInterface.voteWindow.showStartVoteWindow({
                creatorName,
                duration,
                UID,
            });
        }
    }

    updateActiveVoteWindow(messageData: EventStructure[], isApplied: boolean) {
        const msg = messageData[0];
        const userToken = canv.getInitDataProp("userToken");
        const activeVote = canv.canvasController.activeVote;
        const { activeVote: activeVoteUI } = canv.appInterface;
        const isCreateAction = msg.type === "build" && msg.action === "create";

        if (activeVoteUI && isApplied && !isCreateAction && activeVote) {
            if (msg.type === "property") {
                handlePropertyUpdate(msg, activeVote);
            } else if (msg.type === "propertyBuild") {
                handlePropertyBuild(msg, activeVote);
            }
            if (activeVoteUI.isOpen) {
                activeVoteUI.update();
            }
            canv.appInterface.voteWindow.updateTimer();
            return;
        }

        if (isCreateAction && msg.entities[0].creatorToken === userToken && isApplied) {
            activeVoteUI.open();
        }
    }

    updatePastVoteWindow(messageData: EventStructure[], isApplied: boolean) {
        if (!isApplied) {
            return;
        }

        const msg = messageData[0];
        if (msg.type === "build" && msg.action === "delete") {
            canv.canvasController.deletePastVote(msg.entities[0].UID);
            if (canv.appInterface.voteWindow.opened) {
                canv.appInterface.voteWindow.updatePastVoteList();
            }
        }

        if (msg.type === "property" && msg.fixType === "text") {
            canv.canvasController.renamePastVote(msg.UID, msg.state.text);
            if (canv.appInterface.voteWindow.opened) {
                canv.appInterface.voteWindow.updatePastVoteList();
            }
        }
    }

    processAiResponse(message: WSResponse) {
        if (message.status === "error") {
            showSnackbar("Не удалось сгенерировать текст, попробуйте еще раз", {
                type: "error",
                duration: SNACKBAR_TIMEOUT,
            });
        }
        // isApplied === true только в конце стриминга, по этому на него не смотрим и всегда применяем событие
        const messageData = JSON.parse(message.data as string);
        const { widgetUID, streamChunk, text } = messageData;
        const widget = canv.canvasController.FindByUID(widgetUID);
        // Для undo/redo
        // canv.eventManager.createAndInsertAiTwinRecord(this.aiBeforeState, messageData);
        if (streamChunk && widget instanceof AiAnswer) {
            widget.streamText(streamChunk);
        }
        // this.aiBeforeState = [];
        const isStreaming = !!streamChunk;
        const isDone = message.status === "done";
        this.disableAiEvents = isStreaming;

        if (isDone) {
            this.disableAiEvents = false;
            eventEmitter.dispatch("button.disabled", {
                aiAssistant: false,
            });
            return;
        }

        if (isStreaming || !text) {
            eventEmitter.dispatch("button.disabled", {
                aiAssistant: true,
            });
        }
    }
}
